import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Alert, Badge, Button, ButtonGroup, Card, CardBody, CardHeader, CardTitle, Col, Container, FormGroup, Input, Label, Progress, Row, Spinner } from "reactstrap";

import { apiFetch, API } from "../../services/api";
import {
    ANGLES, DEAL_SLOT_FOR, FORMATS, hasShoe, formatLabel, LOCALES, MODES, localeLabel, STUDIO_LIMITS, apiErrorMessage, dealLabel, defaultCategory, charCount, mergePolledStory, needsRefresh, previewState, previewStoryboard,
    saveBlockedReason, slotsFor, statusInfo, textErrors, textsForAngle,
} from "../../helpers/studioStory";
import { ProductPicker } from "./ProductPicker";
import { StoryPlayer } from "./StoryPlayer";

// Studio Story : création d'une story (/studio/new) puis édition, aperçu et export
// (/studio/story/:id). Les prix et captures viennent de l'API (jamais saisis ici).

async function call(method, path, body) {
    const res = await apiFetch(API.back + path, { method, market: false, body: body ? JSON.stringify(body) : undefined, headers: { "Content-Type": "application/json" } });
    let data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    if (!res.ok) throw new Error(apiErrorMessage(res.status, data));
    return data;
}

function Counter({ value, max }) {
    const n = charCount(value);
    return <small className={n > max ? "text-danger font-weight-bold" : "text-muted"}>{n}/{max}</small>;
}

function TextField({ label, value, max, onChange, textarea }) {
    return (
        <FormGroup className="mb-2">
            <div className="d-flex justify-content-between"><Label className="mb-0 small">{label}</Label><Counter value={value} max={max} /></div>
            <Input bsSize="sm" type={textarea ? "textarea" : "text"} rows={2} value={value || ""} invalid={charCount(value) > max} onChange={(e) => onChange(e.target.value)} />
        </FormGroup>
    );
}

// ---------- Création ----------
function NewStory() {
    const history = useHistory();
    const [mode, setMode] = useState("product");
    const [locale, setLocale] = useState("fr");
    const [mixed, setMixed] = useState(false);
    const [picked, setPicked] = useState({});
    const [categories, setCategories] = useState({});
    const [sizes, setSizes] = useState({});
    const [themes, setThemes] = useState([]);
    const [theme, setTheme] = useState("trc");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const [deals, setDeals] = useState(null);

    useEffect(() => { call("GET", "/studio-themes").then(setThemes).catch((e) => setError(e.message)); }, []);
    // Meilleures baisses du jour : pré-remplissent les 3 emplacements du mode généraliste (remplaçables).
    // En espagnol : seulement les produits qui ont un jumeau vendable, avec leurs prix ES.
    useEffect(() => { setDeals(null); call("GET", "/studio-deals?lang=" + locale).then(setDeals).catch(() => setDeals(null)); }, [locale]);
    const suggestionsFor = (s) => {
        const list = mode === "general" && deals ? deals[DEAL_SLOT_FOR[s]] || [] : [];
        return list.map((d) => ({ slug: d.productId, name: d.name, label: dealLabel(d, deals.day) }));
    };
    useEffect(() => {
        if (mode !== "general" || !deals) return;
        setPicked((cur) => {
            const next = { ...cur };
            ["p1", "p2", "p3"].forEach((s) => { const first = (deals[DEAL_SLOT_FOR[s]] || [])[0]; if (!next[s] && first) next[s] = { slug: first.productId, name: first.name }; });
            return next;
        });
        setCategories((cur) => ({ p1: "/products/shoes", p2: "/products/electronic", p3: "/products/electronic", ...cur }));
    }, [mode, deals]);

    const slots = slotsFor(mode, mixed);
    const categoryOf = (s) => categories[s] || (s === "main2" && categories.main) || defaultCategory(s);
    const sized = (s) => ["/products/shoes", "/products/spikes", "/products/apparel"].includes(categoryOf(s));
    const chosen = slots.map((s) => picked[s]).filter(Boolean);

    const create = () => {
        setBusy(true); setError(null);
        const productIds = chosen.map((p) => p.slug);
        const sizeMap = {};
        productIds.forEach((id) => { if (sizes[id]) sizeMap[id] = sizes[id].trim(); });
        call("POST", "/studio-story-draft", { mode, locale, angle: "all", theme, productIds, sizes: sizeMap })
            .then((st) => history.push("/studio/story/" + st.id))
            .catch((e) => { setError(e.message); setBusy(false); });
    };

    return (
        <Container fluid className="p-0">
            <h1 className="h3 mb-3">Nouvelle story</h1>
            <Card>
                <CardBody>
                    <FormGroup>
                        <Label>Type de story</Label>
                        <div><ButtonGroup>{MODES.map((m) => <Button key={m.id} outline={mode !== m.id} color="primary" onClick={() => { if (m.id === mode) return; setMode(m.id); setPicked({}); setMixed(false); }}>{m.label}</Button>)}</ButtonGroup></div>
                    </FormGroup>
                    <FormGroup>
                        <Label>Langue</Label>
                        {/* Changer de langue vide les produits : prix, noms et disponibilité diffèrent entre .fr et .es. */}
                        <div><ButtonGroup>{LOCALES.map((l) => <Button key={l.id} outline={locale !== l.id} color="primary" title={l.hint} onClick={() => { if (l.id !== locale) { setLocale(l.id); setPicked({}); } }}>{l.label}</Button>)}</ButtonGroup></div>
                        {locale === "es" && <small className="text-muted">Pages et prix de therunningcollective.es ; seuls les produits vendus en Espagne sont proposés.</small>}
                    </FormGroup>
                    {slots.map((s) => (
                        <div key={s}>
                            <ProductPicker slot={s} locale={locale} value={picked[s]} category={categoryOf(s)} suggestions={suggestionsFor(s)}
                                onCategory={(c) => setCategories((cur) => ({ ...cur, [s]: c }))}
                                onChange={(p) => setPicked((cur) => ({ ...cur, [s]: p }))} />
                            {picked[s] && sized(s) && (
                                <FormGroup className="mb-3" style={{ maxWidth: 240 }}>
                                    <Label className="small mb-0">Taille montrée au Comparateur (vide = automatique)</Label>
                                    <Input bsSize="sm" placeholder="ex. 42" value={sizes[picked[s].slug] || ""} onChange={(e) => setSizes((cur) => ({ ...cur, [picked[s].slug]: e.target.value }))} />
                                </FormGroup>
                            )}
                        </div>
                    ))}
                    {mode === "product" && (
                        <div className="mb-3">
                            {mixed
                                ? <Button size="sm" color="link" onClick={() => { setMixed(false); setPicked((cur) => ({ main: cur.main })); }}>Retirer la version de l'autre genre</Button>
                                : <Button size="sm" outline color="primary" onClick={() => setMixed(true)}>+ Ajouter la version Homme/Femme (pub mixte)</Button>}
                        </div>
                    )}
                    <FormGroup>
                        <Label>Thème</Label>
                        <ThemeSwatches themes={themes} value={theme} onChange={setTheme} />
                    </FormGroup>
                    {error && <Alert color="danger">{error}</Alert>}
                    <Button color="primary" disabled={busy || chosen.length === 0} onClick={create}>{busy ? <Spinner size="sm" /> : "Créer la story"}</Button>
                    <div className="small text-muted mt-2">Les pages des produits sont capturées sur le site (≈ 30 s par page), puis l'aperçu s'affiche.</div>
                </CardBody>
            </Card>
        </Container>
    );
}

function ThemeSwatches({ themes, value, onChange }) {
    return (
        <div className="d-flex flex-wrap">
            {themes.map((t) => (
                <button key={t.id} type="button" onClick={() => onChange(t.id)} title={t.label}
                    style={{ border: value === t.id ? "2px solid #111" : "1px solid #ddd", borderRadius: 10, padding: 8, marginRight: 8, marginBottom: 8, background: "#fff", cursor: "pointer" }}>
                    <span style={{ display: "flex", marginBottom: 4 }}>
                        {[t.bg, t.accent, t.ink].map((c, i) => <span key={i} style={{ width: 22, height: 22, borderRadius: 11, background: c, border: "1px solid #ccc", marginRight: 3 }} />)}
                    </span>
                    <small>{t.label}</small>
                </button>
            ))}
        </div>
    );
}

// ---------- Édition / aperçu / export ----------
function EditStory({ id }) {
    const [story, setStory] = useState(null);
    const [loadedAt, setLoadedAt] = useState(0);
    const [themes, setThemes] = useState([]);
    const [theme, setTheme] = useState("trc");
    const [texts, setTexts] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [markers, setMarkers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const player = useRef(null);

    // keepEdits = relecture par le polling : storyboard et captures conservés s'ils n'ont pas changé
    // (les URL présignées changent à chaque GET ; les remplacer rechargerait toutes les images).
    const load = useCallback((keepEdits, refresh) => call("GET", "/studio-story?id=" + encodeURIComponent(id)).then((st) => {
        setStory((prev) => (keepEdits ? mergePolledStory(prev, st, refresh) : st));
        if (!keepEdits || refresh) setLoadedAt(Date.now());
        if (!keepEdits) { setTheme(st.theme); setTexts(st.texts); setDirty(false); }
        return st;
    }), [id]);

    useEffect(() => {
        load(false).catch((e) => setError(e.message));
        call("GET", "/studio-themes").then(setThemes).catch(() => {});
    }, [load]);

    const status = story ? story.status : null;
    const info = statusInfo(status);

    // Statuts transitoires : relecture toutes les 3 s ; captures re-signées avant expiration (1 h).
    useEffect(() => {
        if (!story) return undefined;
        const t = setInterval(() => {
            const refresh = needsRefresh(loadedAt, Date.now());
            if (info.polling || refresh) load(true, refresh).catch(() => {});
        }, 3000);
        return () => clearInterval(t);
    }, [story, info.polling, loadedAt, load]);

    const [previewFormat, setPreviewFormat] = useState("story");
    const preview = useMemo(() => (story && story.storyboard && texts ? previewStoryboard(story.storyboard, theme, texts, previewFormat) : null), [story, theme, texts, previewFormat]);
    const onMarkers = useCallback((m) => setMarkers(m), []);

    if (error && !story) return <Alert color="danger">{error}</Alert>;
    if (!story || !texts) return <div className="p-4"><Spinner size="sm" /> Chargement…</div>;

    const blocked = saveBlockedReason(status, texts);
    const errors = textErrors(texts);
    const setT = (fn) => { setTexts((cur) => { const next = JSON.parse(JSON.stringify(cur)); fn(next); return next; }); setDirty(true); };

    const save = (extra) => {
        setBusy(true); setError(null); setNotice(null);
        return call("PUT", "/studio-story?id=" + encodeURIComponent(id), { theme, texts, ...extra })
            .then(() => load(false))
            .then(() => setNotice("Enregistré."))
            .catch((e) => setError(e.message))
            .finally(() => setBusy(false));
    };

    // Textes proposés par Claude : placés dans le formulaire sans être enregistrés (à relire).
    const proposeTexts = () => {
        // Les modifications non enregistrées seraient remplacées par la proposition.
        if (dirty && !window.confirm("Remplacer les textes non enregistrés par une proposition de Claude ?")) return;
        setBusy(true); setError(null); setNotice(null);
        call("POST", "/studio-story-texts?id=" + encodeURIComponent(id))
            .then((res) => {
                setTexts(res.texts); setDirty(true);
                setNotice(res.generated ? "Textes proposés par Claude : relis-les puis enregistre." : "Claude indisponible : textes par défaut proposés.");
            })
            .catch((e) => setError(e.message))
            .finally(() => setBusy(false));
    };

    const changeAngle = (angle) => {
        if (angle === story.angle) return;
        // Textes recomposés appliqués seulement si l'API accepte le nouvel angle (sinon l'état resterait incohérent).
        const next = textsForAngle(story.mode, story.angle, angle, texts, hasShoe(story.storyboard));
        setBusy(true); setError(null);
        call("PUT", "/studio-story?id=" + encodeURIComponent(id), { angle, theme, texts: next })
            .then(() => load(false))
            .catch((e) => setError(e.message))
            .finally(() => setBusy(false));
    };

    const exportMp4 = async (format) => {
        setBusy(true); setError(null); setNotice(null);
        try {
            // Toujours enregistrer avant le rendu : rafraîchit aussi les prix relus (sinon une story rouverte
            // après un changement de prix resterait bloquée sur le 409 « le prix a changé »).
            await call("PUT", "/studio-story?id=" + encodeURIComponent(id), { theme, texts });
            await call("POST", "/studio-story-render?id=" + encodeURIComponent(id) + "&format=" + format);
            await load(false);
            setNotice(`Rendu ${formatLabel(format)} lancé : le MP4 sera prêt dans environ une minute.`);
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Container fluid className="p-0">
            <h1 className="h3 mb-3">Story {story.mode === "general" ? "généraliste" : "produit"} <Badge color={info.color} className="ml-2">{info.label}</Badge> <Badge color="light" className="ml-1">{localeLabel(story.locale)}</Badge></h1>
            {error && <Alert color="danger">{error}</Alert>}
            {notice && <Alert color="success">{notice}</Alert>}
            {story.error && status === "failed" && <Alert color="warning">Dernière tentative en échec : {story.error}</Alert>}
            <Row>
                <Col lg={7}>
                    <Card>
                        <CardHeader><CardTitle tag="h5" className="mb-0">Angle et thème</CardTitle></CardHeader>
                        <CardBody>
                            <Label className="small">Angle</Label>
                            <div className="mb-3"><ButtonGroup size="sm">{ANGLES[story.mode].map((a) => <Button key={a.id} outline={story.angle !== a.id} color="primary" disabled={busy || errors.length > 0 || !["captured", "done", "failed"].includes(status)} onClick={() => changeAngle(a.id)}>{a.label}</Button>)}</ButtonGroup></div>
                            <Label className="small">Thème (aperçu instantané)</Label>
                            <ThemeSwatches themes={themes} value={theme} onChange={(t) => { setTheme(t); setDirty(true); }} />
                        </CardBody>
                    </Card>
                    <Card>
                        <CardHeader><CardTitle tag="h5" className="mb-0">Textes</CardTitle></CardHeader>
                        <CardBody>
                            <TextField label="Accroche — titre" value={texts.hook.title} max={STUDIO_LIMITS.hookTitle} onChange={(v) => setT((t) => { t.hook.title = v; })} />
                            <TextField label="Accroche — punch" value={texts.hook.punch} max={STUDIO_LIMITS.hookPunch} onChange={(v) => setT((t) => { t.hook.punch = v; })} />
                            {texts.chapters.map((c, i) => (
                                <div key={i} className="border-top pt-2 mt-2">
                                    <strong className="small">Chapitre {i + 1}</strong>
                                    <TextField label="Label" value={c.label} max={STUDIO_LIMITS.label} onChange={(v) => setT((t) => { t.chapters[i].label = v; })} />
                                    <TextField label="Titre" value={c.title} max={STUDIO_LIMITS.title} onChange={(v) => setT((t) => { t.chapters[i].title = v; })} />
                                    <TextField label="Sous-titre (non affiché dans la vidéo)" value={c.subtitle} max={STUDIO_LIMITS.subtitle} textarea onChange={(v) => setT((t) => { t.chapters[i].subtitle = v; })} />
                                    <FormGroup className="mb-2">
                                        <Label className="mb-0 small">Badges (séparés par des virgules, {STUDIO_LIMITS.badges} max, {STUDIO_LIMITS.badge} caractères chacun)</Label>
                                        <Input bsSize="sm" value={(c.badges || []).join(", ")} onChange={(e) => setT((t) => { t.chapters[i].badges = e.target.value.split(",").map((b) => b.trim()).filter(Boolean); })} />
                                    </FormGroup>
                                </div>
                            ))}
                            <div className="border-top pt-2 mt-2">
                                <strong className="small">Outro</strong>
                                {texts.outro.lines.map((l, i) => <TextField key={i} label={"Ligne " + (i + 1)} value={l} max={STUDIO_LIMITS.outroLine} onChange={(v) => setT((t) => { t.outro.lines[i] = v; })} />)}
                            </div>
                            <div className="small text-muted mb-2">Un montant en € ou une remise (-N %) doit correspondre aux prix du jour, sinon l'enregistrement est refusé.</div>
                            {errors.length > 0 && <Alert color="warning" className="small">{errors.join(" · ")}</Alert>}
                            <Button color="primary" disabled={busy || !!blocked || !dirty} onClick={() => save()} title={blocked || ""}>Enregistrer</Button>
                            <Button color="secondary" outline className="ml-2" disabled={busy || status === "rendering"} onClick={proposeTexts}>Proposer des textes (Claude)</Button>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={5}>
                    <Card>
                        <CardHeader><CardTitle tag="h5" className="mb-0">Aperçu</CardTitle></CardHeader>
                        <CardBody>
                            <div className="mb-2"><ButtonGroup size="sm">{FORMATS.map((fm) => <Button key={fm.id} outline={previewFormat !== fm.id} color="primary" title={fm.hint} onClick={() => setPreviewFormat(fm.id)}>{fm.label}</Button>)}</ButtonGroup></div>
                            {previewState(status, !!story.captures) === "ready" && preview
                                ? <StoryPlayer ref={player} storyboard={preview} captures={story.captures} onMarkers={onMarkers} />
                                : previewState(status, !!story.captures) === "capture-failed"
                                    ? <Alert color="danger">La capture des pages a échoué{story.error ? " : " + story.error : ""}. Recrée la story depuis « Nouvelle story ».</Alert>
                                    : <div className="text-center p-4"><Spinner size="sm" /> Capture des pages du site en cours…</div>}
                            {markers.length > 0 && (
                                <div className="mt-2 d-flex flex-wrap">
                                    {markers.map((m) => <Button key={m.frame} size="sm" color="light" className="mr-1 mb-1" onClick={() => player.current && player.current.seekTo(m.frame)}>{m.label}</Button>)}
                                </div>
                            )}
                            {status === "rendering" && <div className="mt-3"><small>Rendu du MP4 {formatLabel(((story.renders || [])[story.renders.length - 1] || {}).format)}…</small><Progress animated value={100} /></div>}
                            {FORMATS.map((fm) => {
                                const url = fm.id === "feed" ? story.videoUrlFeed : story.videoUrl;
                                return (
                                    <div className="mt-3" key={fm.id}>
                                        <Button color="success" disabled={busy || !info.exportable || !story.captures || errors.length > 0} onClick={() => exportMp4(fm.id)} title={fm.hint}>Exporter — {fm.label}</Button>
                                        {url && <Button color="link" href={url} target="_blank" rel="noopener noreferrer">Télécharger ({fm.label})</Button>}
                                    </div>
                                );
                            })}
                            {dirty && <div className="small text-muted mt-1">Modifications non enregistrées : elles sont enregistrées automatiquement à l'export.</div>}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export function StoryStudio() {
    const { id } = useParams();
    return id ? <EditStory key={id} id={id} /> : <NewStory />;
}
